import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaEye, FaCalendarAlt, FaClock, FaSearch, FaFilter, FaArrowLeft, FaArrowRight } from 'react-icons/fa';

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewsFilter, setViewsFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const blogsPerPage = 5;

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        let url = `https://my-portfoli-backed.onrender.com/api/blogs?page=${currentPage}&limit=${blogsPerPage}`;
        
        if (searchTerm) {
          url += `&search=${searchTerm}`;
        }
        
        if (viewsFilter) {
          url += `&viewsFilter=${viewsFilter}`;
        }

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          mode: 'cors'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setBlogs(data.data.blogs);
        setTotalItems(data.totalItems);
      } catch (err) {
        console.error('Error details:', err);
        setError('حدث خطأ في جلب البيانات. تأكد من اتصال الخادم');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, [searchTerm, viewsFilter, currentPage]);

  const totalPages = Math.ceil(totalItems / blogsPerPage);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // العودة للصفحة الأولى عند إجراء بحث جديد
  };

  const handleFilterChange = (filter) => {
    setViewsFilter(filter);
    setCurrentPage(1); // العودة للصفحة الأولى عند تغيير الفلتر
  };

  const styles = {
    container: {
      backgroundColor: '#ffffff',
      color: '#333333',
      minHeight: '100vh',
      padding: '4rem 2rem',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      lineHeight: '1.6'
    },
    content: {
      maxWidth: '900px',
      margin: '0 auto',
    },
    header: {
      textAlign: 'center',
      marginBottom: '3rem',
      borderBottom: '2px solid #000000',
      paddingBottom: '1.5rem'
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: '700',
      color: '#000000',
      marginBottom: '0.5rem'
    },
    subtitle: {
      fontSize: '1.2rem',
      color: '#555555',
      fontWeight: '300'
    },
    controls: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      flexWrap: 'wrap',
      gap: '1rem'
    },
    searchContainer: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
      minWidth: '250px'
    },
    searchInput: {
      flex: 1,
      padding: '0.5rem 1rem',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '1rem',
      marginRight: '0.5rem'
    },
    searchButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#000',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    filterButtons: {
      display: 'flex',
      gap: '0.5rem'
    },
    filterButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#f0f0f0',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    activeFilter: {
      backgroundColor: '#000',
      color: '#fff'
    },
    blogList: {
      marginTop: '2rem'
    },
    blogCard: {
      marginBottom: '3rem',
      paddingBottom: '2rem',
      borderBottom: '1px solid #eeeeee',
      transition: 'transform 0.3s ease',
      ':hover': {
        transform: 'translateX(10px)'
      }
    },
    blogTitle: {
      fontSize: '1.8rem',
      fontWeight: '600',
      marginBottom: '0.5rem',
      color: '#000000'
    },
    blogMeta: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '1rem',
      color: '#666666',
      fontSize: '0.9rem',
      flexWrap: 'wrap',
      gap: '1rem'
    },
    blogMetaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.3rem'
    },
    blogCategory: {
      backgroundColor: '#f0f0f0',
      padding: '0.3rem 0.8rem',
      borderRadius: '4px'
    },
    blogExcerpt: {
      marginBottom: '1.5rem',
      color: '#555555',
      fontSize: '1.1rem'
    },
    readMore: {
      display: 'inline-block',
      color: '#000000',
      fontWeight: '500',
      textDecoration: 'none',
      borderBottom: '2px solid #000000',
      paddingBottom: '0.3rem',
      transition: 'color 0.3s ease',
      ':hover': {
        color: '#333333'
      }
    },
    featuredPost: {
      borderLeft: '4px solid #000000',
      paddingLeft: '1.5rem',
      marginLeft: '-1.5rem'
    },
    tagContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginBottom: '1rem'
    },
    tag: {
      backgroundColor: '#f5f5f5',
      padding: '0.3rem 0.8rem',
      borderRadius: '20px',
      fontSize: '0.8rem',
      color: '#555555'
    },
    loading: {
      textAlign: 'center',
      padding: '2rem',
      fontSize: '1.2rem'
    },
    error: {
      textAlign: 'center',
      padding: '2rem',
      fontSize: '1.2rem',
      color: '#ff0000'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '1rem',
      marginTop: '2rem'
    },
    pageButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#f0f0f0',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    activePage: {
      backgroundColor: '#000',
      color: '#fff'
    },
    disabledButton: {
      opacity: '0.5',
      cursor: 'not-allowed'
    }
  };

  if (loading) {
    return <div style={styles.loading}>جاري تحميل المدونات...</div>;
  }

  if (error) {
    return <div style={styles.error}>حدث خطأ: {error}</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <header style={styles.header}>
          <h1 style={styles.title}>مدوناتي</h1>
          <p style={styles.subtitle}>مقالات وتدوينات حول تطوير البرمجيات وأحدث التقنيات</p>
        </header>

        <div style={styles.controls}>
          <form onSubmit={handleSearch} style={styles.searchContainer}>
            <input
              type="text"
              placeholder="ابحث في المدونات..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            <button type="submit" style={styles.searchButton}>
              <FaSearch /> بحث
            </button>
          </form>

          <div style={styles.filterButtons}>
            <button
              style={{
                ...styles.filterButton,
                ...(viewsFilter === 'most' ? styles.activeFilter : {})
              }}
              onClick={() => handleFilterChange('most')}
            >
              <FaFilter /> الأكثر مشاهدة
            </button>
            <button
              style={{
                ...styles.filterButton,
                ...(viewsFilter === 'least' ? styles.activeFilter : {})
              }}
              onClick={() => handleFilterChange('least')}
            >
              <FaFilter /> الأقل مشاهدة
            </button>
            {viewsFilter && (
              <button
                style={styles.filterButton}
                onClick={() => handleFilterChange('')}
              >
                إلغاء الفلتر
              </button>
            )}
          </div>
        </div>

        <div style={styles.blogList}>
          {blogs.length > 0 ? (
            blogs.map((blog) => {
              const publishedDate = new Date(blog.publishedAt).toLocaleDateString('ar-EG', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              });

              return (
                <article 
                  key={blog._id} 
                  style={{
                    ...styles.blogCard,
                    ...(blog.status === 'منشور' ? styles.featuredPost : {})
                  }}
                >
                  <h2 style={styles.blogTitle}>{blog.title}</h2>
                  
                  <div style={styles.blogMeta}>
                    <span style={styles.blogMetaItem}>
                      <FaCalendarAlt size={12} />
                      {publishedDate}
                    </span>
                    <span style={styles.blogMetaItem}>
                      <FaClock size={12} />
                      {blog.readingTime} دقائق قراءة
                    </span>
                    <span style={styles.blogMetaItem}>
                      <FaEye size={12} />
                      {blog.views} مشاهدة
                    </span>
                    <span style={styles.blogCategory}>{blog.type}</span>
                  </div>
                  
                  {blog.technologies && blog.technologies.length > 0 && (
                    <div style={styles.tagContainer}>
                      {blog.technologies.map((tech, index) => (
                        <span key={index} style={styles.tag}>{tech}</span>
                      ))}
                    </div>
                  )}
                  
                  <p style={styles.blogExcerpt}>{blog.content.substring(0, 150)}...</p>
                  
                  <Link to={`/blogs/${blog._id}`} style={styles.readMore}>
                    قراءة المزيد →
                  </Link>
                </article>
              );
            })
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              لا توجد مدونات متطابقة مع معايير البحث
            </div>
          )}
        </div>

        {totalItems > blogsPerPage && (
          <div style={styles.pagination}>
            <button
              style={{
                ...styles.pageButton,
                ...(currentPage === 1 ? styles.disabledButton : {})
              }}
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <FaArrowLeft /> السابق
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                style={{
                  ...styles.pageButton,
                  ...(currentPage === i + 1 ? styles.activePage : {})
                }}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            
            <button
              style={{
                ...styles.pageButton,
                ...(currentPage === totalPages ? styles.disabledButton : {})
              }}
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              التالي <FaArrowRight />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blogs;